import api from './api';

/**
 * Proforma invoices (PIs). Every amount is a 2-dp string computed by the
 * server — the UI displays them but never adds them up itself.
 */

/** The boxes printed in the PI header: camelCase key → API key. */
export const PI_HEADER_FIELDS = {
  deliveryNote: 'delivery_note',
  paymentTerms: 'payment_terms',
  referenceNo: 'reference_no',
  otherReferences: 'other_references',
  buyerOrderNo: 'buyer_order_no',
  buyerOrderDate: 'buyer_order_date',
  dispatchDocNo: 'dispatch_doc_no',
  deliveryNoteDate: 'delivery_note_date',
  dispatchedThrough: 'dispatched_through',
  destination: 'destination',
  deliveryTerms: 'delivery_terms',
};

const headersFromApi = (raw) =>
  Object.fromEntries(Object.entries(PI_HEADER_FIELDS).map(([key, apiKey]) => [key, raw[apiKey] ?? '']));

const headersToApi = (d) =>
  Object.fromEntries(Object.entries(PI_HEADER_FIELDS).map(([key, apiKey]) => [apiKey, d[key] || null]));

export const normalizeProformaInvoice = (raw = {}) => ({
  id: raw.id,
  number: raw.pi_number ?? null, // null for drafts
  financialYear: raw.financial_year ?? null,
  clientId: raw.billing_client_id,
  piDate: raw.pi_date ?? '',
  clientName: raw.client_name ?? '',
  clientGstin: raw.client_gstin ?? '',
  clientAddress: raw.client_address ?? '',
  clientCode: raw.client_code ?? '',
  clientStateCode: raw.client_state_code ?? '',
  clientStateName: raw.client_state_name ?? '',
  ...headersFromApi(raw),
  unit: raw.unit ?? 'MW',
  taxType: raw.tax_type ?? null, // 'IGST' | 'CGST_SGST' | null
  taxLabel: raw.tax_label ?? '',
  gstRate: raw.gst_rate,
  totalQuantity: raw.total_quantity,
  taxableAmount: raw.taxable_amount,
  cgstAmount: raw.cgst_amount,
  sgstAmount: raw.sgst_amount,
  igstAmount: raw.igst_amount,
  taxAmount: raw.tax_amount,
  roundOff: raw.round_off,
  totalAmount: raw.total_amount,
  amountInWords: raw.amount_in_words ?? '',
  advancePercent: raw.advance_percent ?? null, // null = no advance asked for
  advanceAmount: raw.advance_amount,
  advanceCgstAmount: raw.advance_cgst_amount,
  advanceSgstAmount: raw.advance_sgst_amount,
  advanceIgstAmount: raw.advance_igst_amount,
  advanceTaxAmount: raw.advance_tax_amount,
  advanceTotal: raw.advance_total,
  status: raw.status,
  notes: raw.notes ?? '',
  issuedAt: raw.issued_at ?? null,
  cancelledAt: raw.cancelled_at ?? null,
  cancelReason: raw.cancel_reason ?? '',
  createdAt: raw.created_at ?? null,
  itemsCount: raw.items_count ?? raw.items?.length ?? 0,
  items: (raw.items ?? []).map((i) => ({
    id: i.id,
    srNo: i.sr_no,
    description: i.description ?? '',
    hsnCode: i.hsn_code ?? '',
    quantity: i.quantity ?? '',
    rate: i.rate ?? '',
    discountPercent: i.discount_percent ?? '',
    grossAmount: i.gross_amount,
    amount: i.amount,
  })),
  permissions: {
    canEdit: !!raw.permissions?.can_edit,
    canDelete: !!raw.permissions?.can_delete,
    canIssue: !!raw.permissions?.can_issue,
    canCancel: !!raw.permissions?.can_cancel,
    canReinstate: !!raw.permissions?.can_reinstate,
  },
  // Only the detail response carries activity/filename/client.
  activity: (raw.activity ?? []).map((a) => ({
    action: a.action,
    description: a.description,
    user: a.user ?? null,
    properties: a.properties ?? null,
    createdAt: a.created_at,
  })),
  downloadFilename: raw.download_filename ?? null,
  client: raw.client
    ? {
        id: raw.client.id,
        name: raw.client.name,
        gstin: raw.client.gstin ?? '',
        clientCode: raw.client.client_code ?? '',
        stateCode: raw.client.state_code ?? '',
        stateName: raw.client.state_name ?? '',
        address: raw.client.address ?? '',
        isDeleted: Boolean(raw.client.deleted_at),
      }
    : null,
});

const normalizeTotals = (t) => ({
  taxType: t.tax_type,
  taxLabel: t.tax_label,
  gstRate: t.gst_rate,
  halfRate: t.half_rate,
  lines: (t.lines ?? []).map((l) => ({ grossAmount: l.gross_amount, amount: l.amount })),
  totalQuantity: t.total_quantity,
  taxableAmount: t.taxable_amount,
  cgstAmount: t.cgst_amount,
  sgstAmount: t.sgst_amount,
  igstAmount: t.igst_amount,
  taxAmount: t.tax_amount,
  roundOff: t.round_off,
  totalAmount: t.total_amount,
  amountInWords: t.amount_in_words,
  advancePercent: t.advance_percent ?? null,
  advanceAmount: t.advance_amount,
  advanceCgstAmount: t.advance_cgst_amount,
  advanceSgstAmount: t.advance_sgst_amount,
  advanceIgstAmount: t.advance_igst_amount,
  advanceTaxAmount: t.advance_tax_amount,
  advanceTotal: t.advance_total,
  stateMissing: Boolean(t.state_missing),
  clientStateCode: t.client_state_code ?? '',
  clientStateName: t.client_state_name ?? '',
});

const blankToNull = (v) => (v === '' || v == null ? null : v);

const draftToApi = (d) => ({
  billing_client_id: d.clientId,
  pi_date: d.piDate,
  unit: d.unit || null,
  gst_rate: blankToNull(d.gstRate),
  // null = no advance; always sent so clearing the field sticks
  advance_percent: blankToNull(d.advancePercent),
  notes: d.notes || null,
  ...headersToApi(d),
  // Array order is the printed SR No.
  items: d.items.map((i) => ({
    description: i.description,
    hsn_code: i.hsnCode || null,
    quantity: String(i.quantity ?? ''), // as typed; the server parses it
    rate: String(i.rate ?? ''),
    discount_percent: blankToNull(i.discountPercent),
  })),
});

export const proformaInvoiceService = {
  getAll: async (params = {}) => {
    const { data } = await api.get('/billing/proforma-invoices', { params });
    const s = data.summary ?? {};
    return {
      items: data.data.data.map(normalizeProformaInvoice),
      total: data.data.total,
      currentPage: data.data.current_page,
      lastPage: data.data.last_page,
      perPage: data.data.per_page,
      summary: {
        financialYear: s.financial_year,
        issuedCount: s.issued_count,
        totalValue: s.total_value,
        advanceRequested: s.advance_requested,
        draftCount: s.draft_count,
      },
    };
  },
  getById: async (id) => normalizeProformaInvoice((await api.get(`/billing/proforma-invoices/${id}`)).data.data),
  createDraft: async (d) => normalizeProformaInvoice((await api.post('/billing/proforma-invoices', draftToApi(d))).data.data),
  updateDraft: async (id, d) =>
    normalizeProformaInvoice((await api.put(`/billing/proforma-invoices/${id}`, draftToApi(d))).data.data),
  delete: (id) => api.delete(`/billing/proforma-invoices/${id}`),
  issue: async (id) => normalizeProformaInvoice((await api.post(`/billing/proforma-invoices/${id}/issue`)).data.data),
  cancel: async (id, reason) =>
    normalizeProformaInvoice((await api.post(`/billing/proforma-invoices/${id}/cancel`, { reason })).data.data),
  /** Undo a cancellation: back to issued with the same number. */
  reinstate: async (id) => normalizeProformaInvoice((await api.post(`/billing/proforma-invoices/${id}/reinstate`)).data.data),
  duplicate: async (id) => normalizeProformaInvoice((await api.post(`/billing/proforma-invoices/${id}/duplicate`)).data.data),

  /** Stateless totals with the server's own math. Pass `{ signal }` to cancel. */
  calculate: async ({ clientId, gstRate, advancePercent, items }, config = {}) =>
    normalizeTotals((await api.post('/billing/proforma-invoices/calculate', {
      billing_client_id: clientId,
      gst_rate: blankToNull(gstRate),
      advance_percent: blankToNull(advancePercent),
      items: items.map((i) => ({
        quantity: blankToNull(i.quantity),
        rate: blankToNull(i.rate),
        discount_percent: blankToNull(i.discountPercent),
      })),
    }, config)).data.data),
};
